import { useEffect } from "react";

// Input types where forcing uppercase doesn't make sense, or would be
// actively harmful (passwords must preserve exact case as typed).
const SKIP_INPUT_TYPES = new Set([
  "password", "checkbox", "radio", "file", "range", "color",
  "date", "datetime-local", "month", "week", "time",
  "number", "hidden", "submit", "button", "reset", "image"
]);

// Auto-uppercases text typed into any <input>/<textarea> across the app, at
// the DOM level, so it works on every page without editing each form. A
// single document-level capture-phase listener mutates the field's value
// (via the native value setter, which is what makes React's controlled
// inputs pick up the change through their normal onChange) before the event
// reaches the field itself, so React state ends up uppercase too — not just
// the on-screen text.
export const useGlobalAutoUppercase = () => {
  useEffect(() => {
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype, "value"
    ).set;
    const nativeTextareaValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype, "value"
    ).set;

    const handleInput = (e) => {
      const el = e.target;
      if (!el || el.dataset?.noUppercase !== undefined) return;

      const tag = el.tagName;
      if (tag !== "INPUT" && tag !== "TEXTAREA") return;
      if (tag === "INPUT" && SKIP_INPUT_TYPES.has((el.type || "text").toLowerCase())) return;
      if (el.readOnly || el.disabled) return;

      const upper = el.value.toUpperCase();
      if (upper === el.value) return;

      const start = el.selectionStart;
      const end = el.selectionEnd;
      const setter = tag === "TEXTAREA" ? nativeTextareaValueSetter : nativeInputValueSetter;
      setter.call(el, upper);
      if (start !== null && end !== null && typeof el.setSelectionRange === "function") {
        el.setSelectionRange(start, end);
      }
    };

    document.addEventListener("input", handleInput, true);
    return () => document.removeEventListener("input", handleInput, true);
  }, []);
};
